// src/scenes/licenses/Licenses.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DataGrid, esES } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import BadgeIcon from "@mui/icons-material/Badge";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { getMyLicenses, deleteLicense } from "../../services/licenseService"; // 👈 FALTABA ESTE IMPORT
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

/* ===== Paletas coherentes con el resto ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

/* ===== Widget ===== */
function StatCard({ icon, label, value, palette, highlight = false }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "12px",
        backgroundColor: palette.cardBg,
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minHeight: 76,
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2,6,23,0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Box sx={{ opacity: 0.95, display: "grid", placeItems: "center" }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: palette.label,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color: palette.text, fontWeight: 800 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

/* ===== Card contenedora ===== */
function Card({ title, palette, children, action }) {
  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "14px",
        p: 1.5,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 8px 22px rgba(2,6,23,0.08)"
            : "0 3px 10px rgba(0,0,0,0.24)",
        overflow: "hidden",
      }}
    >
      {title && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: action ? "space-between" : "flex-start",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
            {title}
          </Typography>
          {action && <Box sx={{ display: "flex", alignItems: "center" }}>{action}</Box>}
        </Box>
      )}
      <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}>
        {children}
      </Box>
    </Box>
  );
}

/* ===== Estilo DataGrid (root a 100% de alto) ===== */
const dgSx = (palette) => ({
  height: "100%",
  width: "100%",
  display: "flex",
  "& .MuiDataGrid-root": {
    border: "none",
    color: palette.text,
    backgroundColor: "transparent",
    height: "100%",
    width: "100%",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "transparent",
    borderBottom: `1px solid ${palette.cardBorder}`,
    color: palette.label,
    fontWeight: 700,
  },
  "& .MuiDataGrid-cell": { borderBottom: "none" },
  "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${palette.cardBorder}`,
    backgroundColor: "transparent",
  },
  "& .MuiDataGrid-overlay": {
    backgroundColor: "transparent",
    color: palette.label,
  },
});

const Licenses = () => {
  const theme = useTheme();
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const navigate = useNavigate();

  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const columns = [
    { field: "licenseNumber", headerName: "Nº Licencia", flex: 1 },
    { field: "vehiclePlate", headerName: "Matrícula", flex: 1 },
    { field: "vehicleModel", headerName: "Modelo", flex: 1 },
    {
      field: "acciones",
      headerName: "Acciones",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`/licenses/${params.row.id}/edit`)}
            sx={{
              backgroundColor: palette.btn,
              color: "white",
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { backgroundColor: palette.btnHover },
            }}
          >
            Editar licencia
          </Button>
          <IconButton
            aria-label="Eliminar licencia"
            onClick={() => setConfirmId(params.row.id)}
            size="small"
            sx={{
              color: "#ef4444",
              "&:hover": { color: "#dc2626" },
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const raw = await getMyLicenses();
        const enriched = (raw || []).map((l, i) => ({
          id: l._id || `lic_${i}`,
          licenseNumber: l.licenseNumber ?? "—",
          vehicleModel: l.vehicle?.model ?? "—",
          vehiclePlate: l.vehicle?.licensePlate ?? "—",
        }));
        setLicenses(enriched);
      } catch (err) {
        console.error("Error al cargar licencias:", err);
        setError(err?.message || "No se pudieron cargar las licencias.");
        setLicenses([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleDelete = async () => {
    if (!confirmId) return;
    try {
      setDeleting(true);
      await deleteLicense(confirmId);
      setLicenses((prev) => prev.filter((l) => l.id !== confirmId));
      setConfirmId(null);
    } catch (err) {
      console.error("Error al eliminar licencia:", err);
      setError(err?.message || "No se pudo eliminar la licencia.");
    } finally {
      setDeleting(false);
    }
  };

  const totalLicencias = licenses.length;
  const vehiculosUnicos = new Set(
    licenses.map((l) => l.vehiclePlate).filter(Boolean)
  ).size;

  return (
    <Box sx={{ m: "20px", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Header title="Licencias" subtitle="Gestión de licencias activas" />
      {error && (
        <Typography sx={{ color: palette.accent, mt: 1, mb: 0.5 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
        <Grid item xs={12} md={6}>
          <StatCard icon={<BadgeIcon />} label="Total licencias" value={totalLicencias} palette={palette} />
        </Grid>
        <Grid item xs={12} md={6}>
          <StatCard icon={<DirectionsCarIcon />} label="Vehículos únicos" value={vehiculosUnicos} palette={palette} />
        </Grid>
      </Grid>

      {/* Card + DataGrid a altura completa */}
      <Box sx={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column", overflowX: "hidden" }}>
        <Card
          title="Listado de licencias"
          palette={palette}
          action={
            <Button
              variant="contained"
              onClick={() => navigate("/licenses/register")}
              sx={{
                backgroundColor: palette.btn,
                color: "white",
                fontWeight: 700,
                textTransform: "none",
                "&:hover": { backgroundColor: palette.btnHover },
              }}
            >
              Añadir licencia
            </Button>
          }
        >
          <Box sx={dgSx(palette)}>
            <DataGrid
              rows={licenses}
              columns={columns}
              loading={loading}
              getRowId={(row) => row.id}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              rowHeight={52}
              columnHeaderHeight={48}
              disableRowSelectionOnClick
              sx={{ height: "100%", width: "100%" }}
            />
          </Box>
        </Card>
      </Box>
      <Dialog
        open={Boolean(confirmId)}
        onClose={() => setConfirmId(null)}
        PaperProps={{
          sx: {
            backgroundColor: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ color: palette.title, fontWeight: 800 }}>
          Eliminar licencia
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: palette.text }}>
            ¿Seguro que quieres eliminar esta licencia? Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmId(null)} sx={{ color: palette.text }}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Licenses;
